import React, { Component } from "react";
import classnames from "classnames";
import { addSeconds } from "date-fns/esm";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { logger } from "@formant/core/common/logger";
import { duration } from "@formant/core/model/duration";
import { Timeout } from "@formant/core/model/Timeout";

import { observeStore } from "../../stores";
import * as styles from "./AssetVideo.scss";

const liveModeTimeShift = -8;

export interface IAssetVideoProps {
    src: string;
    duration: number;
    startTime: Date;
}

export type IVideoPlayState = "notReady" | "paused" | "playing";

@observer
export class AssetVideo extends Component<IAssetVideoProps> {
    private video?: HTMLVideoElement;
    private delayedPlaybackTimeout: Timeout | undefined;

    private isPlaying: boolean = false;
    private attemptingToPlay: boolean = false;

    private timeState: "past" | "present" | "future" | undefined = undefined;

    public async componentDidMount() {
        await this.syncPlayMode();
        await this.syncPlayhead();
    }

    public async componentDidUpdate() {
        await this.syncPlayMode();
        await this.syncPlayhead();
    }

    public render() {
        const { src, startTime, duration: videoDuration } = this.props;
        const { timeState } = this;
        const visible = timeState === "past" || timeState === "present";

        return (
            <div
                data-custom-timestate={timeState}
                data-custom-starttime={startTime}
                data-custom-duration={videoDuration}
                className={classnames(
                    styles["asset-video"],
                    visible && styles.visible,
                )}
            >
                <video
                    ref={_ => (this.video = _ || undefined)}
                    src={src}
                    preload="auto"
                    onCanPlayThrough={this.onCanPlayThrough}
                    onPause={this.onPause}
                    onEnded={this.onEnded}
                    onPlaying={this.onPlaying}
                    autoPlay={false}
                    muted={true}
                />
            </div>
        );
    }

    private onCanPlayThrough = async () => {
        const { video } = this;

        if (!video) {
            return;
        }

        await this.syncPlayMode();
        await this.syncPlayhead();
    };

    private onPlaying = () => {
        const { video } = this;
        if (!video) {
            return;
        }
        this.isPlaying = true;
        this.attemptingToPlay = false;
    };

    private onPause = () => {
        this.isPlaying = false;
    };

    private onEnded = () => {
        this.timeState = "past";
    };

    private async syncPlayMode() {
        const { video, isPlaying } = this;
        const { mode, speed } = observeStore;

        if (!video) {
            return;
        }

        video.playbackRate = speed;

        if (mode === "pause") {
            this.cancelDelayedPlayback();
            this.pause();
            return;
        }

        if (!isPlaying) {
            await this.play();
        }
    }

    private get relativeTime(): number | undefined {
        const { startTime } = this.props;
        const { time, mode } = observeStore;
        const { video } = this;

        if (!video) {
            return undefined;
        }

        const timeShift =
            mode === "live" ||
            time.getTime() - Date.now() > liveModeTimeShift * duration.second
                ? liveModeTimeShift
                : 0;

        const adjustedTimeMs = addSeconds(time, timeShift).getTime();
        const clipStartTimeMs = startTime.getTime();
        const relativeTime = adjustedTimeMs - clipStartTimeMs;

        return relativeTime;
    }

    private get playheadTime(): number | undefined {
        let { duration: clipDuration } = this.props;
        if (this.video?.duration) {
            // if the video has duration, use it instead since its way more accurate
            clipDuration = defined(this.video?.duration) * duration.second;
        }
        const relativeTime = this.relativeTime;

        if (!relativeTime) {
            return 0;
        }

        const past = relativeTime > clipDuration;
        const present = relativeTime >= 0 && relativeTime <= clipDuration; // timeline falls between start and end
        const future = relativeTime < 0; // timeline is n ms behind start of clip

        this.timeState = past
            ? "past"
            : future
              ? "future"
              : present
                ? "present"
                : undefined;

        const playheadTimeMs = past ? clipDuration : future ? 0 : relativeTime;
        const playheadTimeSeconds = playheadTimeMs / 1000;

        return playheadTimeSeconds;
    }

    private async syncPlayhead() {
        const { video, isPlaying, timeState } = this;
        const { speed, mode } = observeStore;
        const relativeTime = this.relativeTime;

        if (!video || isPlaying) {
            return;
        }

        const currentTime = this.playheadTime;

        if (!video || !currentTime) {
            return;
        }

        if (timeState === "past" && mode === "pause") {
            video.currentTime = currentTime;
            return;
        }

        if (timeState === "present" && !isPlaying) {
            video.currentTime = currentTime;
            await this.play();
            return;
        }

        if (timeState === "future") {
            video.currentTime = 0;
            this.pause();
            if (relativeTime) {
                this.setDelayedPlayback(-relativeTime / speed);
            }
            return;
        }
    }

    private setDelayedPlayback(delayMs: number) {
        this.cancelDelayedPlayback();
        if (delayMs < 0) {
            return;
        }
        this.attemptingToPlay = true;
        this.delayedPlaybackTimeout = setTimeout(async () => {
            this.cancelDelayedPlayback();
            await this.play();
            this.timeState = "present";
        }, delayMs);
    }

    private cancelDelayedPlayback() {
        if (this.delayedPlaybackTimeout) {
            clearTimeout(this.delayedPlaybackTimeout);
            this.delayedPlaybackTimeout = undefined;
        }
    }

    private get canPlay(): boolean {
        const { video } = this;
        return !!video && video.readyState > 3;
    }

    private get shouldPlay(): boolean {
        const { mode } = observeStore;
        const { video, canPlay, isPlaying, attemptingToPlay, timeState } = this;

        return (
            !!video &&
            timeState === "present" &&
            mode !== "pause" &&
            canPlay &&
            !isPlaying &&
            !attemptingToPlay
        );
    }

    private pause() {
        const { video, attemptingToPlay } = this;
        if (!video || attemptingToPlay) {
            return;
        }

        video.pause();
    }

    private async play() {
        const { video } = this;
        if (!video || !this.shouldPlay) {
            return;
        }

        this.attemptingToPlay = true;

        try {
            await video.play();
        } catch (error) {
            logger.warn(error);
        }

        this.attemptingToPlay = false;
    }
}
